import React, { useEffect, useState, useCallback, useRef, memo } from 'react';
import './Notifications.css';
import Header from './Header';
import { FiHeart, FiMessageSquare, FiUserPlus, FiCheck, FiShoppingCart } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { format as timeagoFormat } from 'timeago.js';
import p from './images/profile.jpg';

// Browser-side stale-while-revalidate cache (sessionStorage, per tab)
const SESSION_KEY = 'notif_cache';
function readCache() {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || null; } catch { return null; }
}
function writeCache(data) {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(data)); } catch {}
}

const SLOW_THRESHOLD_MS = 2000;

// Logs an error when an API call exceeds SLOW_THRESHOLD_MS
function checkSlow(label, t0) {
  const elapsed = performance.now() - t0;
  if (elapsed > SLOW_THRESHOLD_MS) {
    console.error(`[SLOW] ${label} took ${(elapsed / 1000).toFixed(2)}s — exceeds ${SLOW_THRESHOLD_MS / 1000}s threshold`);
  }
  return elapsed;
}

// Cache follow-status results in memory for the lifetime of the page so
// repeated renders never fire duplicate network requests for the same pair.
const followStatusCache = new Map(); // key: `${pageId}:${userId}` → true | false

const FollowStatusButton = memo(({ pageId, userId }) => {
  const [isFollowingBack, setIsFollowingBack] = useState(() => {
    const cached = followStatusCache.get(`${pageId}:${userId}`);
    return cached !== undefined ? cached : null;
  });
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const cacheKey = `${pageId}:${userId}`;
    // Skip the network call if we already have a cached result
    if (followStatusCache.has(cacheKey)) return;

    let cancelled = false;
    const t0 = performance.now();
    fetch(`${API_URL}/api/follow_status?page_id=${pageId}&user_id=${userId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        followStatusCache.set(cacheKey, data.is_following);
        setIsFollowingBack(data.is_following);
        checkSlow(`/api/follow_status page_id=${pageId}`, t0);
      })
      .catch(err => console.error("follow_status error:", err));

    return () => { cancelled = true; };
  }, [pageId, userId, API_URL]);

  const handleFollowBack = async () => {
    const t0 = performance.now();
    try {
      const res = await fetch(`${API_URL}/api/follow_page`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ page_id: pageId }),
      });
      checkSlow(`POST /api/follow_page page_id=${pageId}`, t0);
      if (res.ok) {
        followStatusCache.set(`${pageId}:${userId}`, true);
        setIsFollowingBack(true);
      }
    } catch (err) {
      console.error("follow_page error:", err);
    }
  };

  if (isFollowingBack === null) return null;
  return isFollowingBack
    ? <button className="following-button">Following</button>
    : <button className="follow-back-button" onClick={handleFollowBack}>Follow Back</button>;
});

// ------------------------------------------------------------------
// Notification icon + text helpers (pure, defined outside component)
// ------------------------------------------------------------------
const getNotificationIcon = (type) => {
  switch (type) {
    case 'like':         return <FiHeart       className="notification-icon like"    />;
    case 'comment':      return <FiMessageSquare className="notification-icon comment" />;
    case 'follow':       return <FiUserPlus     className="notification-icon follow"  />;
    case 'order':
    case 'order_status': return <FiShoppingCart  className="notification-icon order"  />;
    default:             return <FiHeart        className="notification-icon"         />;
  }
};

const getNotificationText = (type) => {
  switch (type) {
    case 'like':         return 'liked your post';
    case 'comment':      return 'commented on your post';
    case 'follow':       return '';
    case 'order':        return 'placed an order for your product';
    case 'order_status': return 'updated the status of your order : ';
    default:             return 'interacted with your post';
  }
};

// ------------------------------------------------------------------
// Main component
// ------------------------------------------------------------------
const Notifications = () => {
  const [notifications, setNotifications]   = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeFilter, setActiveFilter]     = useState('all');
  const navigate  = useNavigate();
  const token     = localStorage.getItem("token");

  // Keep a stable ref to token so callbacks never go stale
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // ----------------------------------------------------------------
  // fetchNotifications — defined before the auth useEffect so the
  // eslint exhaustive-deps rule is satisfied without suppression.
  // ----------------------------------------------------------------
  const fetchNotifications = useCallback(async () => {
    // Show cached data immediately (zero wait) then refresh in background
    const cached = readCache();
    if (cached) {
      setNotifications(cached);
      setLoading(false);
    }

    const t0 = performance.now();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenRef.current}`,
          'Content-Type': 'application/json'
        }
      });

      checkSlow("GET /api/notifications", t0);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      writeCache(data);
      setNotifications(data);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      if (!cached) setError(err.message || "Failed to fetch notifications. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []); // stable — reads token via ref

  useEffect(() => {
    if (!token) {
      window.location.href = "/login";
      return;
    }
    setIsAuthenticated(true);
    fetchNotifications();
  }, [token, fetchNotifications]);

  // ----------------------------------------------------------------
  // markAsRead — optimistic update, revert on failure
  // ----------------------------------------------------------------
  const markAsRead = useCallback(async (notification_id) => {
    setNotifications(prev =>
      prev.map(n => n.notification_id === notification_id ? { ...n, is_read: true } : n)
    );
    writeCache(null); // invalidate so next open re-fetches
    const t0 = performance.now();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/notifications/mark_read/${notification_id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokenRef.current}`,
            'Content-Type': 'application/json'
          }
        }
      );
      checkSlow(`POST /api/notifications/mark_read/${notification_id}`, t0);
      if (!response.ok) throw new Error('Failed to mark as read');
    } catch (err) {
      console.error("Error marking as read:", err);
      // Revert on failure
      setNotifications(prev =>
        prev.map(n => n.notification_id === notification_id ? { ...n, is_read: false } : n)
      );
    }
  }, []);

  // ----------------------------------------------------------------
  // markAllAsRead — optimistic update
  // ----------------------------------------------------------------
  const markAllAsRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    const t0 = performance.now();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/notifications/mark_all_read`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokenRef.current}`,
            'Content-Type': 'application/json'
          }
        }
      );
      checkSlow("POST /api/notifications/mark_all_read", t0);
      if (!response.ok) throw new Error('Failed to mark all as read');
    } catch (err) {
      console.error("Error marking all as read:", err);
      setError(err.message);
    }
  }, []);

  // ----------------------------------------------------------------
  // handleNotificationClick
  // ----------------------------------------------------------------
  const handleNotificationClick = useCallback((notification) => {
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }
    if (notification.notification_type === 'order') {
      navigate('/orders', { state: { selectedTab: 'merchant' } });
    } else if (notification.notification_type === 'order_status') {
      navigate('/orders', { state: { selectedTab: 'your-orders' } });
    } else if (notification.notification_type === 'follow' && notification.pageid) {
      navigate(`/store/${notification.pageid}`);
    } else if (notification.postid) {
      navigate(`/Home/${notification.postid}`);
    } else {
      navigate('/Home');
    }
  }, [markAsRead, navigate]);

  // ----------------------------------------------------------------
  // Filtering — cheap, runs synchronously on cached state
  // ----------------------------------------------------------------
  const filteredNotifications = notifications.filter(notification => {
    if (activeFilter === 'all')    return true;
    if (activeFilter === 'unread') return !notification.is_read;
    return notification.notification_type === activeFilter;
  });

  if (!isAuthenticated) return null;

  return (
    <>
      <Header />
      <div className="notifications-page">

        <div className="notifications-filters">
          <button
            className={activeFilter === 'all' ? 'active' : ''}
            onClick={() => setActiveFilter('all')}
          >
            All
          </button>
          <button
            className={activeFilter === 'unread' ? 'active' : ''}
            onClick={() => setActiveFilter('unread')}
          >
            Unread
          </button>
        </div>

        {loading ? (
          <div className="notifications-loading">
            <div className="loading-spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : error ? (
          <div className="notifications-error">
            <p>{error}</p>
            <button onClick={fetchNotifications}>Try Again</button>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="no-notifications">
            <div className="no-notifications-icon"><FiMessageSquare /></div>
            <h3>No notifications yet</h3>
            <p>When you get notifications, they'll appear here.</p>
          </div>
        ) : (
          <div className="notifications-list">
            {filteredNotifications.map((n) => (
              <NotificationItem
                key={n.notification_id}
                notification={n}
                onNotificationClick={handleNotificationClick}
                onMarkAsRead={markAsRead}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// ------------------------------------------------------------------
// NotificationItem — memoized so only changed notifications re-render
// ------------------------------------------------------------------
const NotificationItem = memo(({ notification: n, onNotificationClick, onMarkAsRead }) => (
  <div
    className={`notification-item ${n.is_read ? 'read' : 'unread'}`}
    onClick={() => onNotificationClick(n)}
  >
    <div className="notification-avatar">
      <img
        src={n.triggered_by_avatar || p}
        alt={n.triggered_by_username}
        className="notification-avatar-image"
        loading="lazy"
      />
    </div>

    <div className="notification-content">
      <div className="notification-text">
        <span className="username" style={{ marginRight: '5px' }}>{n.triggered_by_username}</span>
        <span>{getNotificationText(n.notification_type)}</span>

        {n.notification_type === 'comment' && n.comment_text && (
          <span className="comment-preview">
            : "{n.comment_text.length > 30 ? n.comment_text.substring(0, 30) + '...' : n.comment_text}"
          </span>
        )}

        {n.notification_type === 'follow' && n.page_name && (
          <span>started following your page <strong>"{n.page_name}"</strong></span>
        )}

        {(n.notification_type === 'order_status' || n.notification_type === 'order') && (
          <span className="notification-preview">
            {n.notification_type === 'order_status' && n.notification_text && (
              <span><strong>{n.notification_text}</strong></span>
            )}
            {n.product_name && (
              <div className="text-preview">Product: <strong>{n.product_name}</strong></div>
            )}
            {n.product_image && (
              <img
                src={n.product_image}
                alt="product"
                width={60}
                height={60}
                loading="lazy"
                style={{ padding: "20px" }}
              />
            )}
          </span>
        )}

        {/* Follow-back button — rendered only for follow notifications */}
        {n.notification_type === 'follow' && n.pageid && (
          <FollowStatusButton pageId={n.pageid} userId={n.triggered_by} />
        )}
      </div>

      <div className="notification-meta">
        <time title={new Date(n.created_at).toLocaleString()} className="timestamp">
          {timeagoFormat(n.created_at)}
        </time>
      </div>
    </div>

    {(n.image_url || n.post_content) && (
      <div className="notification-preview">
        {n.image_url ? (
          <img
            src={n.image_url}
            alt="post preview"
            className="post-thumbnail"
            loading="lazy"
          />
        ) : (
          <div className="text-preview">
            {n.post_content.length > 30 ? n.post_content.substring(0, 30) + '...' : n.post_content}
          </div>
        )}
      </div>
    )}

    {!n.is_read && (
      <div className="notification-actions">
        <button
          onClick={(e) => { e.stopPropagation(); onMarkAsRead(n.notification_id); }}
          className="mark-read-btn"
          title="Mark as read"
        >
          <FiCheck />
        </button>
      </div>
    )}
  </div>
));

export default Notifications;